import { useState } from "react";
import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography, Marker, Line } from "react-simple-maps";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const RSB = "https://www.ratnagears.com/images/logo/rsb.jpg";
const ZAMA = "https://www.ratnagears.com/images/logo/zama.jpg";
const CANRIG = "/clients/canrig.png";
const BRAZIL = "/clients/brazil-mining.png";

const HUB = { coords: [78, 20], label: "Asia Pacific", n: "Manufacturing base", story: "35+ years of precision manufacturing at Ratna Gears, Pune — the source of every Exon Global component.", clients: [] };

const NODES = [
  { coords: [-96, 58], label: "North America", n: "Serving Canada", story: "Exon Global's on-the-ground sales & technical support serving Tier-2 & 3 clients across Canada.", clients: [{ name: "ENEDYM Inc." }] },
  { coords: [-98, 40], label: "US", n: "United States", story: "Direct supply and on-the-ground technical support serving US OEM and defence clients.",
    clients: [{ name: "Canrig Drilling", logo: CANRIG }, { name: "A&M Aerostructure" }, { name: "Spectra Inc." }, { name: "RSB Transmission Ltd.", logo: RSB }] },
  { coords: [-51, -14], label: "South America", n: "Brazil", story: "Crusher assemblies exported to a Brazilian mining operator.", clients: [{ name: "Brazilian Mining Operator", logo: BRAZIL }] },
  { coords: [10, 50], label: "Europe", n: "Germany · Czech · Romania", story: "Reverse-engineered gearbox spares and precision gears delivered across Europe.",
    clients: [{ name: "Zama (Germany)", logo: ZAMA }, { name: "ArcelorMittal" }, { name: "Sandvik" }, { name: "KONE" }] },
  { coords: [47, 29], label: "Middle East", n: "Iraq · Kuwait · Bahrain", story: "Bevel gearboxes supplied to Gulf oil & gas operators.", clients: [{ name: "SULB" }, { name: "Ahleia" }] },
  { coords: [8, 9], label: "Africa", n: "Nigeria · Kenya · S. Africa", story: "Sinter-plant haulage gearboxes indigenised for a Nigerian steelworks.", clients: [{ name: "Tembo Steels" }, { name: "MSI" }] },
  { coords: [116, 30], label: "East Asia", n: "East Asia", story: "Precision drivetrain components supplied to East Asian operators.", clients: [{ name: "Vu Long" }] },
];

export default function WorldMap({ onRegionSelect, activeRegion }) {
  const cases = [HUB, ...NODES];
  const [tip, setTip] = useState(null); // {node, x, y}
  const show = (node) => (e) => setTip({ node, x: e.clientX, y: e.clientY });
  const move = (e) => setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t));
  const hide = () => setTip(null);

  return (
    <div className="relative">
      <div className="relative w-full rounded-2xl overflow-hidden bg-[#0A192F] shadow-card-lg border border-white/10" data-testid="world-map">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 70% at 55% 40%, rgba(212,175,55,0.10), transparent 70%)" }} />
        <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 165 }} width={900} height={440} style={{ width: "100%", height: "auto" }}>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography key={geo.rsmKey} geography={geo} fill="#13284a" stroke="#294c7a" strokeWidth={0.4}
                  style={{ default: { outline: "none", pointerEvents: "none" }, hover: { fill: "#13284a", outline: "none" }, pressed: { outline: "none" } }} />
              ))
            }
          </Geographies>
          {NODES.map((nd, i) => (
            <Line key={`l${i}`} from={HUB.coords} to={nd.coords} stroke="#D4AF37" strokeWidth={1} strokeLinecap="round" strokeDasharray="3 3" style={{ opacity: 0.6 }} />
          ))}
          {NODES.map((nd, i) => <PulseMarker key={i} {...nd} delay={i * 0.15} onClick={onRegionSelect} active={activeRegion === nd.label} onShow={show(nd)} onMove={move} onHide={hide} />)}
          <PulseMarker {...HUB} hub onClick={onRegionSelect} active={activeRegion === HUB.label} onShow={show(HUB)} onMove={move} onHide={hide} />
        </ComposableMap>
      </div>

      {tip && tip.node.clients.length > 0 && (
        <div className="fixed z-[120] pointer-events-none" style={{ left: tip.x + 16, top: tip.y + 12 }}>
          <div className="bg-white rounded-lg shadow-card-lg border border-slate-100 p-3 w-56">
            <div className="font-head font-bold text-[11px] tracked-sm uppercase text-[#0A192F] mb-2">{tip.node.label}</div>
            <div className="space-y-1.5">
              {tip.node.clients.map((c, j) => (
                <div key={j} className="flex items-center gap-2">
                  {c.logo
                    ? <img src={c.logo} alt={c.name} className="h-5 w-10 object-contain shrink-0" />
                    : <span className="w-1.5 h-1.5 rounded-full gold-gradient shrink-0" />}
                  <span className="text-[#0A192F] text-[11px] leading-snug">{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 text-left">
        {cases.map((nd, i) => (
          <motion.div key={i} data-testid={`case-${i}`} onClick={() => onRegionSelect && onRegionSelect(nd.label)} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
            className={`cursor-pointer rounded-lg p-5 shadow-card border transition-colors ${nd.label === HUB.label ? "bg-[#0A192F] border-[#D4AF37]/40" : "bg-white border-slate-100 hover:border-[#D4AF37]"}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1.5 h-1.5 rounded-full gold-gradient" />
              <span className={`font-head text-[11px] tracked uppercase ${nd.label === HUB.label ? "text-white" : "text-[#0A192F]"}`}>{nd.label}</span>
              <span className={`text-[10px] ml-auto ${nd.label === HUB.label ? "text-[#D4AF37]" : "text-slate-400"}`}>{nd.n}</span>
            </div>
            <p className={`text-sm leading-relaxed ${nd.label === HUB.label ? "text-slate-300" : "text-slate-500"}`}>{nd.story}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function PulseMarker({ coords, label, hub, delay = 0, onClick, active, onShow, onMove, onHide }) {
  const r = hub ? 5.5 : 3.5;
  return (
    <Marker coordinates={coords} onClick={() => onClick && onClick(label)}
      onMouseEnter={onShow} onMouseMove={onMove} onMouseLeave={onHide}
      style={{ default: { cursor: "pointer" } }}>
      {active && <circle r={r + 3} fill="none" stroke="#FFD700" strokeWidth={1.2} />}
      <motion.circle r={r} fill="#FFD700" stroke="#0A192F" strokeWidth={0.6} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay, duration: 0.4 }} />
      <motion.circle r={r} fill="none" stroke="#FFD700" strokeWidth={0.8} animate={{ r: [r, r * 3], opacity: [0.7, 0] }} transition={{ duration: 2, repeat: Infinity, delay }} />
      <text textAnchor="middle" y={-r - 4} style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: hub ? 9.5 : 8, letterSpacing: "0.04em", textTransform: "uppercase", fill: hub ? "#FFD700" : "#ffffff", pointerEvents: "none" }}>{label}</text>
    </Marker>
  );
}
